/**
 * №13.1 (§2.3) — fee-arrangement detail (`place/fee-arrangement/:id`). One
 * component, two variants selected by `billing_method`:
 *   • T&M — budget-version history (Draft → Approved → Superseded), the live
 *     APPROVED version's lines, and budget-vs-actual. Approved/superseded versions
 *     are governed (create a new draft to revise).
 *   • Fixed — the fee-line table (Draft → Posted), with per-line Post.
 * Both — Close (guarded: refused while outstanding) / Reopen; a closed arrangement
 * is governed (the money identity is frozen).
 *
 * The lifecycle guards live in the engine's first-party verbs; the chrome renders
 * the teaching UI (§1.5) and drives the verbs, never inventing business logic.
 */
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import { useRowDetail } from "../../hooks/use-row-detail";
import { GovernedNotice } from "./GovernedNotice";
import { governanceFor } from "./governance-map";
import {
  useBudgetVersions, useBudgetLines, useFeeLines, useEstimateBreakdown,
  useCreateBudgetVersion, useApproveBudgetVersion, useDeleteDraftBudgetVersion, useCreateBudgetLine,
  useCreateFeeLine, usePostFeeLine, useCloseArrangement, useReopenArrangement,
  type EntityRow,
} from "../../hooks/use-time-billing";

const dollarsToMinor = (s: string): number => Math.round((parseFloat(s) || 0) * 100);

export function ArrangementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useRowDetail("fee-arrangement", id);
  const arr = detail.data?.entity as (EntityRow & Record<string, unknown>) | undefined;
  const method = (arr?.billing_method as string | undefined) ?? "time_and_materials";
  const status = (arr?.status as string | undefined) ?? "open";
  const governed = governanceFor("fee-arrangement", status);
  const isFixed = method === "fixed";

  if (detail.isLoading) return <div className="screen"><div className="card"><div className="skeleton sk-line" style={{ width: "40%" }} /></div></div>;
  if (detail.isError || !arr) return <div className="screen"><div className="error-state"><h3>Couldn’t load this arrangement</h3><button data-variant="primary" onClick={() => detail.refetch()}>Retry</button></div></div>;

  return (
    <div className="screen vt">
      <div className="surface" style={{ marginBottom: 14 }}>
        <div className="surface__bar">
          <div className="ledger-identity">
            <span className="statutory">{(arr.description as string) ?? arr.id} — {isFixed ? "fixed" : "T&M"}</span>
            <span className="bank">{arr.id} · {(arr.matter as string) ?? ""}{arr.is_default === "yes" ? " · default" : ""}</span>
          </div>
          <span className="spacer" style={{ flex: 1 }} />
          <span className={`badge ${isFixed ? "badge--fixed" : "badge--tm"}`}>{isFixed ? "Fixed" : "T&M"}</span>
          <span className="badge badge--soft" style={{ marginLeft: 6 }}>{status}</span>
        </div>
      </div>

      {governed && (
        <GovernedNotice message="A closed fee arrangement's money identity is frozen." fix="Reopen arrangement" onFix={undefined} />
      )}

      {isFixed
        ? <FixedVariant arrangementId={id!} closed={!!governed} onChange={() => void detail.refetch()} />
        : <TmVariant arrangementId={id!} closed={!!governed} onChange={() => void detail.refetch()} />}

      <CloseReopen arrangementId={id!} status={status} onChange={() => void detail.refetch()} />
    </div>
  );
}

// ---- T&M variant ----------------------------------------------------------
function TmVariant({ arrangementId, closed, onChange }: { arrangementId: string; closed: boolean; onChange: () => void }) {
  const versions = useBudgetVersions(arrangementId);
  const estimate = useEstimateBreakdown(arrangementId);
  const rows = useMemo(() => versions.data ?? [], [versions.data]);
  const approved = useMemo(() => rows.find((v) => v.status === "approved"), [rows]);
  const draft = useMemo(() => rows.find((v) => v.status === "draft"), [rows]);
  const approvedLines = useBudgetLines(approved?.id as string | undefined);
  const draftLines = useBudgetLines(draft?.id as string | undefined);

  const createVersion = useCreateBudgetVersion();
  const approveVersion = useApproveBudgetVersion();
  const deleteDraft = useDeleteDraftBudgetVersion();
  const addLine = useCreateBudgetLine();

  const [ln, setLn] = useState({ category: "time", description: "", amount: "" });

  const est = estimate.data;
  const actual = est?.actual_wip_minor ?? 0;
  const estMinor = est?.estimate_minor ?? 0;
  const pct = estMinor > 0 ? Math.min(100, Math.round((actual / estMinor) * 100)) : 0;
  const sumLines = (ls?: EntityRow[]) => (ls ?? []).reduce((s, l) => s + ((l.amount_minor as number) ?? 0), 0);

  const run = async (p: Promise<unknown>, ok: string) => { try { await p; showToast(ok, "success"); onChange(); } catch (e) { showToast((e as Error).message, "error"); } };

  return (
    <>
      <div className="card card--accent">
        <div className="card__h">Budget vs actual <span className="sub">live = latest APPROVED version only (drafts ignored)</span></div>
        <div className="card__b">
          <div className="figs" style={{ fontSize: 13, marginBottom: 6 }}><span className="money">{money(actual)} WIP</span><span className="muted">of {money(estMinor)} approved · {pct}%</span></div>
          <div className={`progress${actual > estMinor && estMinor > 0 ? " over" : ""}`}><span style={{ width: `${pct}%` }} /></div>
        </div>
      </div>

      <div className="card card--flush">
        <div className="card__h">Budget versions<span className="spacer" />
          {!closed && !draft && <button data-variant="primary" disabled={createVersion.isPending} onClick={() => void run(createVersion.mutateAsync({ fee_arrangement_id: arrangementId }), "Draft budget version started")}>＋ New draft version</button>}
        </div>
        {rows.length === 0 ? <div className="card__b muted tiny">No budget versions yet.</div> : (
          <table className="ledger-table"><thead><tr><th>Version</th><th>Status</th><th className="num">Amount (Σ lines)</th><th>Approved</th><th /></tr></thead><tbody>
            {rows.slice().sort((a, b) => ((b.version_no as number) ?? 0) - ((a.version_no as number) ?? 0)).map((v) => {
              const amt = v.id === approved?.id ? sumLines(approvedLines.data) : v.id === draft?.id ? sumLines(draftLines.data) : null;
              return (
                <tr key={v.id}>
                  <td>v{(v.version_no as number) ?? "?"}</td>
                  <td><span className={`pill ${v.status === "approved" ? "pill--issued" : v.status === "draft" ? "pill--pending" : "pill--reversed"}`}><span className="dot" />{v.status as string}</span></td>
                  <td className="num money">{amt != null ? money(amt) : "—"}</td>
                  <td className="muted">{(v.approved_by as string) ?? ""}{v.approved_at ? ` · ${String(v.approved_at).slice(0, 10)}` : ""}</td>
                  <td className="muted tiny">
                    {v.status === "approved" || v.status === "superseded" ? <span title="Approved budgets are immutable">🔒 governed</span> : null}
                    {!closed && v.id === draft?.id && (
                      <span className="dotline">
                        <button className="act" style={{ padding: "2px 8px" }} disabled={approveVersion.isPending} onClick={() => void run(approveVersion.mutateAsync({ budget_version_id: v.id as string, fee_arrangement_id: arrangementId }), "Budget approved")}>Approve</button>
                        <button className="act" style={{ padding: "2px 8px" }} disabled={deleteDraft.isPending} onClick={() => void run(deleteDraft.mutateAsync({ budget_version_id: v.id as string }), "Draft discarded")}>Delete</button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody></table>
        )}
      </div>

      {draft && !closed && (
        <div className="card">
          <div className="card__h">Draft v{(draft.version_no as number) ?? "?"} lines <span className="sub">editable while Draft — amount is Σ of these</span></div>
          <div className="card__b">
            {(draftLines.data ?? []).length > 0 && (
              <table className="ledger-table" style={{ marginBottom: 10 }}><thead><tr><th>Category</th><th>Description</th><th className="num">Amount</th></tr></thead><tbody>
                {draftLines.data!.map((l) => <tr key={l.id}><td>{(l.category as string) || "—"}</td><td>{(l.description as string) ?? ""}</td><td className="num money">{money((l.amount_minor as number) ?? 0)}</td></tr>)}
              </tbody></table>
            )}
            <div className="dotline">
              <div className="formfield" style={{ marginBottom: 0 }}><label>Category</label><select value={ln.category} onChange={(e) => setLn({ ...ln, category: e.target.value })}><option value="time">time</option><option value="disbursement">disbursement</option></select></div>
              <div className="formfield" style={{ marginBottom: 0, flex: 1 }}><label>Description</label><input value={ln.description} onChange={(e) => setLn({ ...ln, description: e.target.value })} /></div>
              <div className="formfield" style={{ marginBottom: 0 }}><label>Amount ($)</label><input value={ln.amount} inputMode="decimal" onChange={(e) => setLn({ ...ln, amount: e.target.value })} /></div>
              <button data-variant="primary" disabled={addLine.isPending} onClick={() => void run(addLine.mutateAsync({ budget_version_id: draft.id as string, category: ln.category, description: ln.description, amount_minor: dollarsToMinor(ln.amount) }).then(() => setLn({ category: "time", description: "", amount: "" })), "Budget line added")}>Add line</button>
            </div>
          </div>
        </div>
      )}

      {approved && (
        <div className="card card--flush">
          <div className="card__h">v{(approved.version_no as number) ?? "?"} lines <span className="sub">approved — immutable</span></div>
          <table className="ledger-table"><thead><tr><th>Category</th><th>Description</th><th className="num">Amount</th></tr></thead><tbody>
            {(approvedLines.data ?? []).map((l) => <tr key={l.id}><td>{(l.category as string) || "—"}</td><td>{(l.description as string) ?? ""}</td><td className="num money">{money((l.amount_minor as number) ?? 0)}</td></tr>)}
          </tbody></table>
          <div className="card__b"><GovernedNotice message="Approved budget lines are locked." fix="Create a new draft version to revise" /></div>
        </div>
      )}
    </>
  );
}

// ---- Fixed variant --------------------------------------------------------
function FixedVariant({ arrangementId, closed, onChange }: { arrangementId: string; closed: boolean; onChange: () => void }) {
  const lines = useFeeLines(arrangementId);
  const addLine = useCreateFeeLine();
  const postLine = usePostFeeLine();
  const [ln, setLn] = useState({ description: "", qty: "1", unit: "" });
  const rows = lines.data ?? [];
  const run = async (p: Promise<unknown>, ok: string) => { try { await p; showToast(ok, "success"); onChange(); } catch (e) { showToast((e as Error).message, "error"); } };

  return (
    <div className="card card--flush">
      <div className="card__h">Fee lines<span className="spacer" /></div>
      {rows.length === 0 ? <div className="card__b muted tiny">No fee lines yet — add one below.</div> : (
        <table className="ledger-table"><thead><tr><th>#</th><th>Description</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Amount</th><th>Status</th></tr></thead><tbody>
          {rows.slice().sort((a, b) => ((a.line_no as number) ?? 0) - ((b.line_no as number) ?? 0)).map((l) => (
            <tr key={l.id}>
              <td>{(l.line_no as number) ?? "—"}</td><td>{(l.description as string) ?? ""}</td>
              <td className="num">{(l.qty as number) ?? "—"}</td><td className="num money">{money((l.unit_price_minor as number) ?? 0)}</td><td className="num money">{money((l.amount_minor as number) ?? 0)}</td>
              <td>
                <span className={`pill ${l.status === "posted" ? "pill--issued" : "pill--pending"}`}><span className="dot" />{(l.status as string) ?? "draft"}</span>
                {!closed && l.status !== "posted" && <button className="act" style={{ padding: "2px 8px", marginLeft: 6 }} disabled={postLine.isPending} onClick={() => void run(postLine.mutateAsync({ fee_line_id: l.id as string, fee_arrangement_id: arrangementId }), "Fee line posted")}>Post</button>}
              </td>
            </tr>
          ))}
        </tbody></table>
      )}
      {!closed && (
        <div className="card__b"><div className="dotline">
          <div className="formfield" style={{ marginBottom: 0, flex: 1 }}><label>Description</label><input value={ln.description} onChange={(e) => setLn({ ...ln, description: e.target.value })} /></div>
          <div className="formfield" style={{ marginBottom: 0 }}><label>Qty</label><input value={ln.qty} inputMode="decimal" onChange={(e) => setLn({ ...ln, qty: e.target.value })} /></div>
          <div className="formfield" style={{ marginBottom: 0 }}><label>Unit ($)</label><input value={ln.unit} inputMode="decimal" onChange={(e) => setLn({ ...ln, unit: e.target.value })} /></div>
          <button data-variant="primary" disabled={addLine.isPending} onClick={() => void run(addLine.mutateAsync({ fee_arrangement_id: arrangementId, description: ln.description, qty: parseFloat(ln.qty) || 0, unit_price_minor: dollarsToMinor(ln.unit) }).then(() => setLn({ description: "", qty: "1", unit: "" })), "Fee line added")}>＋ Add fee line</button>
        </div></div>
      )}
    </div>
  );
}

// ---- close / reopen (guarded) --------------------------------------------
function CloseReopen({ arrangementId, status, onChange }: { arrangementId: string; status: string; onChange: () => void }) {
  const close = useCloseArrangement();
  const reopen = useReopenArrangement();
  const run = async (p: Promise<unknown>, ok: string) => { try { await p; showToast(ok, "success"); onChange(); } catch (e) { showToast((e as Error).message, "error"); } };
  return (
    <div className="card"><div className="card__b"><div className="dotline">
      {status === "closed"
        ? <button data-variant="primary" disabled={reopen.isPending} onClick={() => void run(reopen.mutateAsync({ fee_arrangement_id: arrangementId }), "Arrangement reopened")}>Reopen arrangement</button>
        : <><button disabled={close.isPending} onClick={() => void run(close.mutateAsync({ fee_arrangement_id: arrangementId }), "Arrangement closed")}>Close arrangement</button><span className="muted tiny">— refused while it has outstanding WIP or posted-but-unbilled fixed lines; the guard names the reason.</span></>}
    </div></div></div>
  );
}
